import api from 'utils/api';
import { getCachedUserTokens } from 'utils/auth';
import { IAssistantStatus, IAssistantWorkspace, IConversation, IConversationSummary, IFeedback, IUpload, TAssistantEvent } from './types';

interface IDataResponse<T> {
    data: T;
}

export const assistantApi = {
    status: (): Promise<IDataResponse<IAssistantStatus>> => api.get('assistant/status'),
    workspaces: (): Promise<IDataResponse<{ workspaces: IAssistantWorkspace[] }>> => api.get('assistant/workspaces'),
    list: (): Promise<IDataResponse<{ conversations: IConversationSummary[] }>> => api.get('assistant/conversations'),
    create: (): Promise<IDataResponse<IConversation>> => api.post('assistant/conversations', {}),
    get: (id: string): Promise<IDataResponse<IConversation>> => api.get(`assistant/conversations/${id}`),
    remove: (id: string): Promise<unknown> => api.delete(`assistant/conversations/${id}`),
    switchWorkspace: (id: string, companyId: string): Promise<IDataResponse<{ workspace: IAssistantWorkspace }>> =>
        api.post(`assistant/conversations/${id}/workspace`, { company_id: companyId }),
    /** Thumbs up / down on one answer; re-rating replaces the earlier rating. */
    rate: (
        id: string,
        messageId: string,
        feedback: IFeedback,
    ): Promise<IDataResponse<{ message_id: string; rating: 1 | -1; comment: string | null }>> =>
        api.post(`assistant/conversations/${id}/messages/${messageId}/feedback`, {
            rating: feedback.rating,
            comment: feedback.comment || null,
        }),
};

/** Upload a file to attach to the next message; the server parses it once and keeps it a few hours. */
export const uploadFile = (conversationId: string, file: File): Promise<IDataResponse<IUpload>> => {
    const form = new FormData();
    form.append('file', file, file.name);
    return api.post(`assistant/conversations/${conversationId}/files`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
};

/** Download a file the assistant produced, with the same bearer token the axios instance uses. */
export async function downloadFile(url: string, name: string): Promise<void> {
    const tokens = getCachedUserTokens();
    const response = await fetch(url, {
        headers: tokens?.access_token ? { Authorization: `Bearer ${tokens.access_token}` } : {},
    });
    if (!response.ok) {
        let message = 'This download has expired; ask the assistant to generate it again.';
        try {
            const json = await response.json();
            message = json?.data?.msg || message;
        } catch (e) {
            /* not json */
        }
        throw new Error(message);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = name;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}

/** Body for the confirm stream: approve, decline, or hand the create off to the prefilled form. */
export const confirmBody = (approve: boolean, handoff = false): Record<string, unknown> =>
    handoff ? { approve: false, handoff: true } : { approve };

/**
 * POST and read a server-sent-event stream. axios cannot stream, so this uses fetch
 * with the same bearer token the axios instance attaches.
 */
export async function streamAssistant(
    path: string,
    body: Record<string, unknown>,
    onEvent: (event: TAssistantEvent) => void,
    signal?: AbortSignal,
): Promise<void> {
    const tokens = getCachedUserTokens();
    const response = await fetch(`/api/v1/assistant/${path}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            ...(tokens?.access_token ? { Authorization: `Bearer ${tokens.access_token}` } : {}),
        },
        body: JSON.stringify(body),
        signal,
    });
    if (!response.ok || !response.body) {
        let message = `Request failed (${response.status})`;
        try {
            const json = await response.json();
            message = json?.data?.msg || message;
        } catch (e) {
            /* not json */
        }
        onEvent({ type: 'error', message });
        return;
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    // eslint-disable-next-line no-constant-condition
    while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
            const frame = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            const dataLine = frame.split('\n').find(line => line.startsWith('data: '));
            if (dataLine) {
                try {
                    onEvent(JSON.parse(dataLine.slice(6)) as TAssistantEvent);
                } catch (e) {
                    /* ignore malformed frame */
                }
            }
            boundary = buffer.indexOf('\n\n');
        }
    }
}
